import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Mail, Info, AlertCircle, CheckCircle, ExternalLink, Shield } from "lucide-react";

interface CompanyInviteProps {
  onInvitationSent?: () => void;
}

const CompanyInvite = ({ onInvitationSent }: CompanyInviteProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [emails, setEmails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const notify = useNotify();
  const { user, profile, isAdmin: canManageMembers } = useAuth();

  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!profile?.company_id) return;
      
      try {
        const { data: company } = await supabase
          .from('companies')
          .select('name')
          .eq('id', profile.company_id)
          .single();
        
        if (company) {
          setCompanyName(company.name);
        }
      } catch (error) {
        console.error('Error fetching company name:', error);
      }
    };

    fetchCompanyName();
  }, [profile?.company_id]);

  // If user cannot manage members, show a message instead of the form
  if (!canManageMembers) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Admin Access Required
          </CardTitle>
          <CardDescription>
            Only company administrators can invite team members
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Permission Required</p>
              <p>Contact your company administrator to invite new team members to your organization.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sendInvitationEmail = async (email: string, invitationId: string, token: string) => {
    try {
      console.log("Sending invitation email to:", email);
      
      const { data, error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email,
          companyName: companyName || 'Our Company',
          inviterName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'A team member',
          invitationId,
          token
        }
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        throw new Error(`Failed to send invitation: ${emailError.message}`);
      }
      
      console.log("Email sent successfully:", data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error sending invitation email:', error);
      return { success: false, error: error.message };
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setError(null);
    setSuccess(null);
    
    if (!user?.id) {
      notify.error("You must be logged in to invite team members.");
      return;
    }
    
    const emailList = emails.split(",")
      .map(email => email.trim())
      .filter(email => email.length > 0);
    
    if (emailList.length === 0) {
      setError("Please enter at least one email address.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log("Starting invitation process for emails:", emailList);
      
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error("Failed to fetch profile:", profileError);
        throw profileError;
      }

      const companyId = userProfile.company_id;
      if (!companyId) {
        throw new Error("No company found. Please set up your company information first.");
      }
      
      const results = await Promise.allSettled(emailList.map(async (email) => {
        try {
          // Check for existing pending invitation
          const { data: existingInvitation, error: checkError } = await supabase
            .from('company_invitations')
            .select('id, status, token')
            .eq('company_id', companyId)
            .eq('email', email)
            .maybeSingle();
          
          if (checkError) {
            console.error("Error checking existing invitation:", checkError);
            throw checkError;
          }
          
          let invitationId;
          let invitationToken;
          
          if (existingInvitation && existingInvitation.status === 'pending') {
            console.log("Using existing pending invitation for:", email);
            invitationId = existingInvitation.id;
            invitationToken = existingInvitation.token;
          } else {
            console.log("Creating new invitation for email:", email);
            
            const { data: invitation, error: inviteError } = await supabase
              .from('company_invitations')
              .insert({
                company_id: companyId,
                email: email,
                invited_by: user.id,
                token: crypto.randomUUID() // Generate token for new invitations
              })
              .select()
              .single();

            if (inviteError) {
              console.error("Failed to create invitation:", inviteError);
              throw inviteError;
            }
            
            invitationId = invitation.id;
            invitationToken = invitation.token;
          }
          
          // Send invitation email
          const emailResult = await sendInvitationEmail(email, invitationId, invitationToken);
          if (!emailResult.success) {
            throw new Error(`Failed to send email to ${email}: ${emailResult.error}`);
          }
          
          return { email, success: true };
        } catch (error: any) {
          console.error(`Error processing invitation for ${email}:`, error);
          return { email, success: false, error: error.message };
        }
      }));
      
      // Handle Promise.allSettled results correctly
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results
        .filter(r => r.status === 'fulfilled' && !r.value.success)
        .map(r => r.status === 'fulfilled' ? r.value : null)
        .filter(Boolean);
      
      if (successful > 0) {
        const successMessage = `Successfully sent ${successful} invitation${successful > 1 ? 's' : ''}`;
        setSuccess(successMessage);
        
        notify.success(successMessage);
        
        setEmails("");
        
        if (onInvitationSent) {
          onInvitationSent();
        }

        // Dispatch event to refresh team members
        window.dispatchEvent(new CustomEvent('invitationSent'));
      }
      
      if (failed.length > 0) {
        const failedEmails = failed.map(f => f?.email).filter(Boolean).join(', ');
        const errorDetails = failed.map(f => f?.error).filter(Boolean).join('; ');
        setError(`Failed to send invitations to: ${failedEmails}. ${errorDetails}`);
      }
      
    } catch (error: any) {
      console.error("Invitation error:", error);
      setError(error.message || "Failed to send invitations");
      notify.error(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleInvite}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Team Members
          </CardTitle>
          <CardDescription>
            Invite colleagues to join your company
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emails" className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                Email Addresses
              </Label>
              <Input
                id="emails"
                placeholder="Enter email addresses separated by commas"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                required
              />
              <p className="text-sm text-gray-500">
                Enter multiple email addresses separated by commas.
              </p>
            </div>
            
            {error && (
              <div className="flex items-start space-x-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error</p>
                  <p>{error}</p>
                  {error.includes('domain') && (
                    <div className="mt-2">
                      <p className="text-xs">
                        Email setup required. Please check:
                      </p>
                      <ul className="text-xs mt-1 space-y-1">
                        <li>• RESEND_API_KEY is configured in Supabase</li>
                        <li>• Domain is verified in Resend</li>
                        <li>
                          • Visit{" "}
                          <a 
                            href="https://resend.com/domains" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:no-underline"
                          >
                            Resend Domains <ExternalLink className="h-3 w-3 inline" />
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {success && (
              <div className="flex items-start space-x-2 text-sm text-green-600 bg-green-50 p-3 rounded-md">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Success!</p>
                  <p>{success}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start space-x-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p>
                  Invitations will be sent via email with a link to join your company. 
                  Recipients will need to create an account or sign in to accept the invitation.
                </p>
                <p className="mt-2 font-medium">
                  Note: Check spam folders if emails don't arrive.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Invitations"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default CompanyInvite;
